/**
 * useSuppliers Hook
 *
 * React Query hooks for suppliers.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  suppliersService,
  type QuerySuppliersDto,
  type CreateSupplierDto,
  type UpdateSupplierDto,
} from '../services/suppliers.service';

/**
 * Query key factory for suppliers
 */
export const supplierKeys = {
  all: ['suppliers'] as const,
  lists: () => [...supplierKeys.all, 'list'] as const,
  list: (params: QuerySuppliersDto) => [...supplierKeys.lists(), params] as const,
  details: () => [...supplierKeys.all, 'detail'] as const,
  detail: (id: string) => [...supplierKeys.details(), id] as const,
  kpis: () => [...supplierKeys.all, 'kpis'] as const,
  active: () => [...supplierKeys.all, 'active'] as const,
};

/**
 * Hook to fetch suppliers list with pagination
 */
export function useSuppliers(params: QuerySuppliersDto) {
  return useQuery({
    queryKey: supplierKeys.list(params),
    queryFn: () => suppliersService.list(params),
    placeholderData: (prev) => prev,
  });
}

/**
 * Hook to fetch single supplier
 */
export function useSupplier(id: string | null) {
  return useQuery({
    queryKey: supplierKeys.detail(id!),
    queryFn: () => suppliersService.getById(id!),
    enabled: !!id,
  });
}

/**
 * Hook to fetch suppliers KPIs
 */
export function useSupplierKPIs() {
  return useQuery({
    queryKey: supplierKeys.kpis(),
    queryFn: () => suppliersService.getKPIs(),
  });
}

/**
 * Hook to fetch active suppliers for dropdowns
 * @param options.enabled - Si false, ne pas fetch (évite 403 pour OPERATOR)
 */
export function useActiveSuppliers(options?: { enabled?: boolean }) {
  const enabled = options?.enabled ?? true;
  return useQuery({
    queryKey: supplierKeys.active(),
    queryFn: () => suppliersService.getActive(),
    enabled,
  });
}

/**
 * Hook to create a supplier
 */
export function useCreateSupplier() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateSupplierDto) => suppliersService.create(data),
    onSuccess: (supplier) => {
      queryClient.invalidateQueries({ queryKey: supplierKeys.all });
      toast.success(`Fournisseur "${supplier.name}" cree avec succes`);
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      toast.error(error.response?.data?.error?.message ?? 'Erreur lors de la creation');
    },
  });
}

/**
 * Hook to update a supplier
 */
export function useUpdateSupplier() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateSupplierDto }) =>
      suppliersService.update(id, data),
    onSuccess: (supplier) => {
      queryClient.invalidateQueries({ queryKey: supplierKeys.all });
      toast.success(`Fournisseur "${supplier.name}" mis a jour`);
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      toast.error(error.response?.data?.error?.message ?? 'Erreur lors de la mise a jour');
    },
  });
}

/**
 * Hook to deactivate a supplier
 */
export function useDeactivateSupplier() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => suppliersService.deactivate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: supplierKeys.all });
      toast.success('Fournisseur desactive');
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      toast.error(error.response?.data?.error?.message ?? 'Erreur lors de la desactivation');
    },
  });
}

/**
 * Hook to activate a supplier
 */
export function useActivateSupplier() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => suppliersService.activate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: supplierKeys.all });
      toast.success('Fournisseur reactive');
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      toast.error(error.response?.data?.error?.message ?? 'Erreur lors de la reactivation');
    },
  });
}
